/**
 * What the Management Portal launch deliberately does NOT include.
 *
 * Decision by Rob, 2026-09-02, after the design-match audit: the surfaces whose
 * controls render but can never work are OUT of launch scope. They are not to
 * ship as dead controls, and they are not to be counted as gaps against the
 * approved design.
 *
 * The audit found 30 such components. They divide into two kinds:
 *
 *  1. A whole screen with no backend at all -- O18 Scenarios, O19 Readiness,
 *     O21 Resets, O22 Evidence make no network call whatsoever; there is no
 *     read route for any of them in lambda/dashboard/demo-env/handler.py.
 *     These lose their routes: an operator cannot reach a screen that can
 *     only ever say "nothing here".
 *
 *  2. A working screen carrying one or more controls that cannot fire --
 *     O01's four command buttons, O04's approve/reject/revoke, O05's schedule
 *     controls, and so on. The screen keeps its read, and the disabled button
 *     is replaced by a plain statement of what is missing.
 *
 * Why a register rather than an edit to the workbook: the Screen Contract
 * sheet is the approved design and stays intact. This records a scope DECISION
 * against it, the same way DECISIONS.D12 records the public-corpus encryption
 * call. Reversing the decision means deleting an entry here, not restoring a
 * document.
 */
import React from 'react';
import { Info } from 'lucide-react';

export interface ScopeDecision {
  /** Screen id from the approved contract. */
  id: string;
  /** Human name, so the register reads without the workbook open. */
  name: string;
  /** Why it cannot work today -- the concrete missing thing, never "TODO". */
  because: string;
  /** 'screen' loses its route; 'controls' keeps the screen, drops the buttons. */
  removes: 'screen' | 'controls';
}

export const OUT_OF_LAUNCH_SCOPE: readonly ScopeDecision[] = [
  // O18/O19/O20/O21/O22 (the whole demo domain) were restored by Story
  // #5650: a real scenario catalogue, readiness verifier, reset-preview
  // read model, evidence chain and the O20 hold-acquire approval gate now
  // all exist. This register entry stays deleted, not reworded to
  // "resolved" — reversal here means removing the entry, per this file's
  // own docstring.
  {
    // Narrowed by Story #5650. This entry used to withdraw FOUR controls
    // together -- open, compare, export and divergence-report -- on one shared
    // reason about hash/signer verification. That reason only ever applied to
    // the export: a signed pack goes to someone who cannot see where the
    // numbers came from, so it needs a signature. Opening a record, comparing
    // two and reporting how they disagree are questions about evidence the
    // screen has already loaded, and are now BUILT (evidenceActions.ts).
    // Three working features were withdrawn to protect one that needed it.
    id: 'O17',
    name: 'Recovery evidence — signed export only',
    because: 'no per-record hash or signer exists, so an exported pack could not be verified by whoever receives it, and the classification, DLP and retention rules for handing one out have not been set',
    removes: 'controls',
  },
  // O01/O02/O04/O05/O06 (Story #5651), K06/K07 and P26 (Story #5654) were
  // deleted from this register on main when their writers shipped. They
  // reappeared here only because the demo branch was cut BEFORE those lanes
  // landed, so a 3-way merge re-added them as if they were still true. They
  // are not: the typed-command path, assign/acknowledge/snooze, the
  // approval-plan store, schedule and incident writers, the .docx guide
  // indexer and the per-role meter are all built and live. Re-added entries
  // would withdraw eight working screens' controls all over again, which is
  // exactly the failure this file's docstring warns about.
];

const BY_ID = new Map(OUT_OF_LAUNCH_SCOPE.map((d) => [d.id, d]));

/** The scope decision for a screen, if it has one. */
export function scopeDecisionFor(screenId: string | undefined): ScopeDecision | undefined {
  return screenId ? BY_ID.get(screenId) : undefined;
}

/** Screen ids whose whole route is withdrawn from the launch. */
export const WITHDRAWN_SCREEN_IDS: readonly string[] = OUT_OF_LAUNCH_SCOPE.filter(
  (d) => d.removes === 'screen',
).map((d) => d.id);

/**
 * Says plainly that something is not in the launch, and why.
 *
 * This replaces a permanently-disabled button. A greyed-out "Approve" invites
 * the operator to wonder what permission they are missing; this tells them
 * there is nothing to be missing yet.
 */
export const NotInLaunchScope: React.FC<{
  /** What the removed control claimed to do, e.g. 'Approve, reject or revoke'. */
  control: string;
  /** The concrete missing thing. */
  because: string;
  className?: string;
}> = ({ control, because, className }) => (
  <p
    role="note"
    data-testid="not-in-launch-scope"
    className={`flex items-start gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600 ${className ?? ''}`}
  >
    <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" aria-hidden="true" />
    <span>
      <span className="font-semibold">{control}</span> is not in the launch — {because}.
    </span>
  </p>
);
